import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, catchError, debounceTime, defer, of, switchMap } from 'rxjs';
import { IAdminOrgOption } from '@interfaces/index';
import { ORG_OPTION_LOOKUP, OrgOptionLookup } from './org-option-lookup';

const ORG_LOOKUP_DEBOUNCE_MS = 800;
const ORG_LOOKUP_CAP = 20;

// Extracted from PeopleListComponent's inline autocomplete (TK-020), then
// relocated to ui-components (TK-054) so people, move-panel and the
// assessments filter bar can all share it. Owns the lookup, the option list
// and the capped/no-match rows. The host keeps ownership of anything that
// round-trips through its own state (query parameters, a required org_id
// control) — the picker only reports what the operator typed and picked, via
// `control` and the outputs below. Depends on ORG_OPTION_LOOKUP rather than
// an application service, so no HTTP crosses into this library.
@Component({
  selector: 'admin-org-picker',
  standalone: false,
  templateUrl: './org-picker.component.html',
  styleUrl: './org-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgPickerComponent implements OnInit {

  @Input() control!: FormControl<string>;
  @Input() selectedName = '';
  @Input() activeOnly = false;
  @Input() label = 'Organization';
  @Input() inputTestId = 'org-picker-input';
  @Input() optionTestId = 'org-picker-option';
  @Input() noMatchesTestId = 'org-picker-no-matches';
  @Input() cappedHintTestId = 'org-picker-capped-hint';
  @Input() errorTestId = 'org-picker-error';
  @Output() optionSelected = new EventEmitter<IAdminOrgOption>();
  @Output() orgInvalidated = new EventEmitter<void>();

  orgOptions: IAdminOrgOption[] = [];
  orgOptionsCapped = false;
  orgLookupAttempted = false;

  constructor(
    @Inject(ORG_OPTION_LOOKUP) protected _org_lookup: OrgOptionLookup,
    private _destroy_ref: DestroyRef,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._subscribeToOrgLookup();
  }

  trackByOrgOptionId(_index: number, option: IAdminOrgOption): number {
    return option.id;
  }

  onOrgOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as IAdminOrgOption;
    this.control.setValue(option.name ?? '', { emitEvent: false });
    this.optionSelected.emit(option);
  }

  // Called by the host on a filter/form reset, since the option list and the
  // lookup-attempted flag now live here rather than on the host.
  reset(): void {
    this.orgOptions = [];
    this.orgOptionsCapped = false;
    this.orgLookupAttempted = false;
    this._cdr.markForCheck();
  }

  private _subscribeToOrgLookup(): void {
    this.control.valueChanges.pipe(
      debounceTime(ORG_LOOKUP_DEBOUNCE_MS),
      // MatAutocompleteTrigger writes the whole option object into the control on
      // selection, before onOrgOptionSelected writes the name back; only typed text
      // is looked up. The guard is per lookup so one failure never ends the stream.
      switchMap((value) => typeof value === 'string'
        ? defer(() => this._lookupOrgOptions(value)).pipe(catchError(() => of<IAdminOrgOption[]>([])))
        : of(this.orgOptions)),
      takeUntilDestroyed(this._destroy_ref)
    ).subscribe((options) => {
      this.orgOptions = options;
      this.orgOptionsCapped = options.length === ORG_LOOKUP_CAP;
      this._cdr.markForCheck();
    });
  }

  private _lookupOrgOptions(text: string): Observable<IAdminOrgOption[]> {
    const trimmed = text.trim();
    if (trimmed !== this.selectedName) {
      this.orgInvalidated.emit();
    }
    this.orgLookupAttempted = !!trimmed;
    if (!trimmed) {
      return of<IAdminOrgOption[]>([]);
    }
    return this._org_lookup.getOrgOptions(undefined, trimmed, this.activeOnly);
  }

}
